/**
 * CANONICAL MODULE — app-platform/shared/graphRetry.ts
 *
 * Copied byte-for-byte into every app (`src/lib/graphRetry.ts`). It imports nothing, and every
 * function in it is pure: the clock and the random source are parameters. Built for work order W10
 * (task 2.b) from [design/W10-sync-transport.md](../design/W10-sync-transport.md) §3.2.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * Replaces five retry policies. The accidents it converges (§2): no jitter anywhere; trakr ignoring
 * 5xx; inkling refusing to retry without a `Retry-After`; quil's uncapped wait and its
 * `NaN`-on-HTTP-date bug (an unparseable header became an immediate retry); contently's global pause
 * that a later, SMALLER penalty could shorten.
 *
 * What it keeps on purpose:
 * - **A cap exists** (bindr §2.4). A blocking cold-start gate turns an honoured two-minute backoff
 *   into two minutes of spinner. The cap's *value* is config — bindr passes `capMs: 4000`.
 * - **423 is not retried.** A locked file stays locked for minutes; it surfaces as a `GraphError`,
 *   which `errorLog` already files as sync noise. **507 is not retried** — a full drive stays full.
 * - **Writes are retried blind.** Every write in the estate is an idempotent whole-body PUT, and a
 *   conditional one that already landed comes back 412 on the retry — which is correct, and which
 *   the caller already handles.
 */

export interface RetryPolicy {
  /** Attempts per request, the first one included. A forced token refresh after a 401 is not one. */
  maxAttempts: number
  /** The first backoff ceiling, doubled per attempt. */
  baseMs: number
  /** No single wait is ever longer than this — not even one `Retry-After` asked for. */
  capMs: number
}

export const DEFAULT_RETRY: RetryPolicy = { maxAttempts: 5, baseMs: 500, capMs: 15_000 }

const RETRYABLE_STATUSES: ReadonlySet<number> = new Set([408, 429, 500, 502, 503, 504])

/** Transient by Microsoft's documentation, or a network throw. Everything else fails once. */
export function isRetryable(r: { status?: number; networkError?: boolean }): boolean {
  if (r.networkError) return true
  return r.status !== undefined && RETRYABLE_STATUSES.has(r.status)
}

/** Throttle statuses: the ones that also pause every other request through the same client. */
export function isThrottle(status: number): boolean {
  return status === 429 || status === 503
}

/**
 * `Retry-After` as milliseconds from `now`, or `null` when absent or unparseable.
 *
 * RFC 9110 allows seconds or an HTTP-date, and Graph sends both. A plain number is seconds and is
 * consumed here either way: `Date.parse('-5')` is not `NaN` in V8 (it reads as a year), so letting a
 * negative number fall through to the date branch would quietly produce a wait of zero (bindr's
 * comment, kept). A date in the past is a wait of zero, which is honest.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function parseRetryAfter(header: string | null | undefined, now: number): number | null {
  if (header === null || header === undefined) return null
  const text = header.trim()
  if (!text) return null
  if (/^[+-]?\d+(\.\d+)?$/.test(text)) {
    const seconds = Number(text)
    return seconds >= 0 ? seconds * 1000 : null
  }
  const date = Date.parse(text)
  if (!Number.isFinite(date)) return null
  return Math.max(date - now, 0)
}

/**
 * How long to wait before retry number `attempt` (0 for the first retry).
 *
 * Full jitter — `random() * min(capMs, baseMs * 2^attempt)` — so five tabs throttled at the same
 * instant do not come back at the same instant. A parseable `Retry-After` is a FLOOR on that, and
 * the result is still capped. Never `NaN`, never negative.
 */
export function retryDelayMs(
  attempt: number,
  retryAfter: string | null,
  p: RetryPolicy,
  now: number,
  random: () => number,
): number {
  const ceiling = Math.min(p.capMs, p.baseMs * 2 ** Math.max(0, attempt))
  const r = random()
  const jittered = (Number.isFinite(r) ? Math.min(Math.max(r, 0), 1) : 0) * ceiling
  const floor = parseRetryAfter(retryAfter, now) ?? 0
  return Math.max(0, Math.min(p.capMs, Math.max(jittered, floor)))
}

export interface ThrottleGate {
  /** Pause every request until at least `now + ms`. Never SHORTENS an existing pause. */
  pauseFor(ms: number): void
  /** How long the next request must still wait; 0 when it may go. */
  remainingMs(): number
}

/**
 * The estate-wide throttle pause: contently's `graphPausedUntil`, fixed. A throttle on one request
 * means Graph is throttling the user, so every other request through the same client waits too.
 *
 * `pausedUntil = max(pausedUntil, now + min(ms, capMs))` — contently overwrote it, so a later small
 * penalty could cut a large one short.
 */
export function createThrottleGate(now: () => number, capMs: number = DEFAULT_RETRY.capMs): ThrottleGate {
  let pausedUntil = 0
  return {
    pauseFor(ms: number): void {
      if (!Number.isFinite(ms) || ms <= 0) return
      pausedUntil = Math.max(pausedUntil, now() + Math.min(ms, capMs))
    },
    remainingMs(): number {
      return Math.max(0, pausedUntil - now())
    },
  }
}
